import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationDialogComponent } from '@fuse/Dialog/confirmation-dialog/confirmation-dialog.component';
import { ToolbarService } from 'app/layout/components/toolbar/toolbar.service';
import {
    AUTH_TOKEN_MAX_LENGTH, AuthType, DefaultSender, FROM_NAME_MAX_LENGTH, HOST_MAX_LENGTH, PASSWORD_MAX_LENGTH,
    SecurityType, SmtpApiError, SmtpSettings, USERNAME_MAX_LENGTH,
} from '../models';
import { SmtpApiService } from '../smtp-api.service';
import {
    authTokenRequired, buildSaveRequest, formFromSettings, hasSavedSecret, isFormDirty, loginAllowed, MAILTRAP_PRESET,
    passwordRequired, portAfterSecurityChange, SmtpFormValue, validateForm,
} from '../smtp-form';

type Action = 'save' | 'verify' | 'enable' | 'delete';

/** Input id for each form field, used to focus the first invalid one. */
const FIELD_IDS: Partial<Record<keyof SmtpFormValue, string>> = {
    host: 'smtpHost', port: 'smtpPort', username: 'smtpUsername', password: 'smtpPassword',
    authToken: 'smtpAuthToken', fromEmail: 'smtpFromEmail', fromName: 'smtpFromName', replyTo: 'smtpReplyTo',
};

@Component({
    selector: 'app-smtp-settings',
    templateUrl: './smtp-settings.component.html',
    styleUrls: ['./smtp-settings.component.scss']
})
export class SmtpSettingsComponent implements OnInit, OnDestroy {

    readonly HOST_MAX_LENGTH = HOST_MAX_LENGTH;
    readonly USERNAME_MAX_LENGTH = USERNAME_MAX_LENGTH;
    readonly PASSWORD_MAX_LENGTH = PASSWORD_MAX_LENGTH;
    readonly AUTH_TOKEN_MAX_LENGTH = AUTH_TOKEN_MAX_LENGTH;
    readonly FROM_NAME_MAX_LENGTH = FROM_NAME_MAX_LENGTH;

    readonly securityOptions: { value: SecurityType; label: string }[] = [
        { value: 'starttls', label: 'STARTTLS (usually port 587)' },
        { value: 'tls', label: 'SSL/TLS (usually port 465)' },
        { value: 'none', label: 'None, no encryption (usually port 25)' },
    ];

    loading = false;
    loadError = '';
    settings: SmtpSettings | null = null;
    defaultSender: DefaultSender | null = null;
    form: SmtpFormValue = formFromSettings(null);
    fieldErrors: Partial<Record<keyof SmtpFormValue, string>> = {};
    submitted = false;

    busy: Action | null = null;
    /** Latest server message for a failed action. */
    actionError = '';
    private _actionErrorFrom: Action | null = null;
    /** Reason text from the latest failed verify. */
    verifyError = '';

    /** Seconds left before an action can be retried after a 429. */
    cooldown: Partial<Record<Action, number>> = {};
    private _cooldownTimer: any = null;

    constructor(
        private _smtpApi: SmtpApiService,
        private _toolbarService: ToolbarService,
        private _matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private _elementRef: ElementRef<HTMLElement>,
    ) {
        this._toolbarService._ToolBarTitle.next('Custom SMTP');
        this._toolbarService._isBackBtnShow.next(false);
    }

    ngOnInit(): void {
        this.load();
    }

    ngOnDestroy(): void {
        this.stopCooldownTimer();
        this.clearSecrets();
    }

    // ---------------------------------------------------------------- state

    get hasSavedRow(): boolean {
        return !!this.settings && !this.settings.isDefault;
    }

    get isDirty(): boolean {
        return isFormDirty(this.form, this.settings);
    }

    get loginAllowed(): boolean {
        return loginAllowed(this.form);
    }

    get passwordRequired(): boolean {
        return passwordRequired(this.form, this.settings);
    }

    get authTokenRequired(): boolean {
        return authTokenRequired(this.form, this.settings);
    }

    get secretSaved(): boolean {
        return hasSavedSecret(this.form, this.settings);
    }

    /** True when the admin switched auth type away from the saved one, so a new secret is needed. */
    get authTypeSwitched(): boolean {
        return this.hasSavedRow && this.loginAllowed && this.settings.authType !== this.form.authType
            && (this.form.authType === 'token' || !!this.form.username.trim());
    }

    /** Sender shown while mail still goes out through the platform. */
    get platformSender(): DefaultSender | null {
        if (this.defaultSender) {
            return this.defaultSender;
        }
        return this.settings?.isDefault ? this.settings : null;
    }

    get canVerify(): boolean {
        return this.hasSavedRow && !this.isDirty && !this.busy && !this.cooldown.verify;
    }

    get verifyHint(): string {
        if (!this.hasSavedRow) {
            return 'Save your settings first.';
        }
        if (this.isDirty) {
            return 'Save your changes first. Verify checks the saved settings.';
        }
        return '';
    }

    /** Turning on needs a verified row. Turning off is always allowed. */
    get enableToggleDisabled(): boolean {
        if (!this.hasSavedRow || !!this.busy) {
            return true;
        }
        return !this.settings.isEnabled && !this.settings.isVerified;
    }

    get canSave(): boolean {
        return !this.busy && !this.cooldown.save && (!this.hasSavedRow || this.isDirty);
    }

    // ---------------------------------------------------------------- load

    load(): void {
        this.loading = true;
        this.loadError = '';
        this._smtpApi.getSettings().then((result) => {
            this.defaultSender = result.data?.default || null;
            this.applySettings(result.data?.settings || null);
        }, (error: SmtpApiError) => {
            this.settings = null;
            this.loadError = this.errorText(error);
        }).finally(() => this.loading = false);
    }

    /** Uses a server reply as the new state and resets the form to it. */
    private applySettings(settings: SmtpSettings | null): void {
        this.settings = settings;
        this.form = formFromSettings(settings);
        this.fieldErrors = {};
        this.submitted = false;
    }

    /** Uses a server reply as the new state without touching unsaved edits. */
    private applyStatus(settings: SmtpSettings | null | undefined): void {
        if (settings) {
            this.settings = settings;
        }
    }

    // ---------------------------------------------------------------- form events

    onSecurityTypeChange(next: SecurityType): void {
        const previous = this.form.securityType;
        this.form.securityType = next;
        this.form.port = portAfterSecurityChange(this.form.port, previous, next);
        if (next === 'none') {
            // No login without encryption; token sign-in always needs a username.
            this.form.authType = 'password';
            this.clearSecrets();
        }
        this.revalidate();
    }

    onAuthTypeChange(next: AuthType): void {
        this.form.authType = next;
        // The matching secret must be typed fresh; never carry one across types.
        this.clearSecrets();
        this.revalidate();
    }

    applyMailtrapPreset(): void {
        this.form = { ...this.form, ...MAILTRAP_PRESET, password: '', authToken: '' };
        this.revalidate();
    }

    revalidate(): void {
        if (this.submitted) {
            this.fieldErrors = validateForm(this.form, this.settings);
        }
    }

    onDiscard(): void {
        this.applySettings(this.settings);
    }

    // ---------------------------------------------------------------- actions

    onSave(): void {
        this.submitted = true;
        this.fieldErrors = validateForm(this.form, this.settings);
        const firstInvalid = (Object.keys(FIELD_IDS) as (keyof SmtpFormValue)[]).find(field => this.fieldErrors[field]);
        if (firstInvalid) {
            setTimeout(() => {
                const input = this._elementRef.nativeElement.querySelector<HTMLInputElement>('#' + FIELD_IDS[firstInvalid]);
                input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                input?.focus({ preventScroll: true });
            });
            return;
        }

        this.startAction('save');
        this._smtpApi.saveSettings(buildSaveRequest(this.form, this.settings)).then((result) => {
            this.applySettings(result.data?.settings || null);
            this.verifyError = '';
            this._matSnackBar.open(result.message || 'Settings saved.', 'OK', { duration: 3000 });
        }, (error: SmtpApiError) => this.handleError('save', error)).finally(() => this.busy = null);
    }

    onVerify(): void {
        if (!this.canVerify) {
            return;
        }
        this.startAction('verify');
        this.verifyError = '';
        this._smtpApi.verifySettings().then((result) => {
            this.applyStatus(result.data?.settings);
            this._matSnackBar.open(result.message || 'Connection verified.', 'OK', { duration: 3000 });
        }, (error: SmtpApiError) => {
            if (error.status === 400) {
                // One banner with the reason; the server message repeats the same text.
                this.verifyError = error.data?.error || error.message;
                this.applyStatus(error.data?.settings);
                this.revealErrors();
                return;
            }
            this.handleError('verify', error);
        }).finally(() => this.busy = null);
    }

    onEnabledChange(event: MatSlideToggleChange): void {
        const toggle: MatSlideToggle = event.source;
        const enabled = event.checked;
        if (enabled && !this.settings?.isVerified) {
            toggle.checked = false;
            return;
        }

        this.startAction('enable');
        this._smtpApi.setEnabled(enabled).then((result) => {
            this.applyStatus(result.data?.settings);
            this._matSnackBar.open(result.message || (enabled ? 'Custom SMTP switched on.' : 'Custom SMTP switched off.'), 'OK', { duration: 3000 });
        }, (error: SmtpApiError) => {
            if (error.status === 409 || error.status === 404) {
                // The row is not verified (or gone); fetch the real state.
                this.refreshStatus();
            }
            this.handleError('enable', error);
        }).finally(() => {
            this.busy = null;
            toggle.checked = !!this.settings?.isEnabled;
        });
    }

    onDelete(): void {
        this._matDialog.open(ConfirmationDialogComponent, {
            panelClass: ['commonDialogWrapper'],
            backdropClass: 'dialogBackdrop',
            disableClose: false,
            data: {
                message: 'Your SMTP settings and saved credentials will be removed, and emails will go out from the EventLocal default sender again. This cannot be undone.',
                image: '/assets/ui/delete.png',
                focus: 'right',
                // The dialog title-cases this, so avoid the "SMTP" acronym (it would read "Smtp").
                deleteItem: 'Switch back to the default sender?',
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
            }
        }).afterClosed().subscribe((confirmed) => {
            if (!confirmed) {
                return;
            }
            this.startAction('delete');
            this._smtpApi.deleteSettings().then((result) => {
                this.verifyError = '';
                this.applySettings(result.data?.settings || null);
                this._matSnackBar.open(result.message || 'Custom SMTP removed.', 'OK', { duration: 3000 });
            }, (error: SmtpApiError) => {
                if (error.status === 404) {
                    // Nothing saved on the server: the default sender is already in use.
                    this.verifyError = '';
                    this._matSnackBar.open('You are already using the EventLocal default sender.', 'OK', { duration: 3000 });
                    this.load();
                    return;
                }
                this.handleError('delete', error);
            }).finally(() => this.busy = null);
        });
    }

    /** Reloads status only, keeping the admin's unsaved edits. */
    private refreshStatus(): void {
        this._smtpApi.getSettings().then((result) => {
            this.defaultSender = result.data?.default || this.defaultSender;
            if (isFormDirty(this.form, this.settings)) {
                this.applyStatus(result.data?.settings);
            } else {
                this.applySettings(result.data?.settings || null);
            }
        }, () => { /* the action error is already shown */ });
    }

    private startAction(action: Action): void {
        this.busy = action;
        this.actionError = '';
        this._actionErrorFrom = null;
    }

    private handleError(action: Action, error: SmtpApiError): void {
        const retryAfter = error.retryAfterSeconds;
        if (retryAfter) {
            this.startCooldown(action, retryAfter);
        }
        this.actionError = this.errorText(error);
        this._actionErrorFrom = action;
        this.revealErrors();
    }

    private errorText(error: SmtpApiError): string {
        if (error.status === 401) {
            // A real HTTP 401 often has no readable body; the cause is always the session.
            return 'Your session has expired. Please log in again.';
        }
        if (error.status === 403) {
            return 'Custom SMTP is only available to the main admin account.';
        }
        return error.message;
    }

    /** Error banners sit above the form; bring the one for this action into view (not an older lastError banner). */
    private revealErrors(): void {
        setTimeout(() => {
            this._elementRef.nativeElement.querySelector('.notice.action-result')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }

    // ---------------------------------------------------------------- 429 countdown

    private startCooldown(action: Action, seconds: number): void {
        this.cooldown = { ...this.cooldown, [action]: seconds };
        if (this._cooldownTimer) {
            return;
        }
        this._cooldownTimer = setInterval(() => {
            const next: Partial<Record<Action, number>> = {};
            for (const key of Object.keys(this.cooldown) as Action[]) {
                if (this.cooldown[key] > 1) {
                    next[key] = this.cooldown[key] - 1;
                } else if (this._actionErrorFrom === key) {
                    // The rate-limit message no longer applies.
                    this.actionError = '';
                    this._actionErrorFrom = null;
                }
            }
            this.cooldown = next;
            if (!Object.keys(next).length) {
                this.stopCooldownTimer();
            }
        }, 1000);
    }

    private stopCooldownTimer(): void {
        if (this._cooldownTimer) {
            clearInterval(this._cooldownTimer);
            this._cooldownTimer = null;
        }
    }

    formatCountdown(seconds: number): string {
        const minutes = Math.floor(seconds / 60);
        const rest = seconds % 60;
        return minutes ? `${minutes}:${String(rest).padStart(2, '0')}` : `${rest}s`;
    }

    private clearSecrets(): void {
        this.form.password = '';
        this.form.authToken = '';
    }
}
